import { atomFamily, selectorFamily, useRecoilState } from 'recoil'
import { AccountId, IProfitShare } from '../types/types'
import { api } from '../utils/api'
import { CurrencyOperations } from '../utils/currency'

export const fetchProfitShare = async (accountId: AccountId): Promise<IProfitShare[]> => {
  try {
    const { data } = await api.get(`/account/${accountId}/profit-shares`)
    return data.shares
  } catch (error) {
    console.error(error)
    return []
  }
}

export const profitSharesState = atomFamily<IProfitShare[], AccountId>({
  key: 'profitSharesState',
  default: selectorFamily({
    key: 'profitSharesState/Default',
    get: (accountId) => async (): Promise<IProfitShare[]> => fetchProfitShare(accountId),
  }),
})

/**
 * Add a new deposit to the list of profits
 */
export const useAddProfit = (accountId: AccountId) => {
  const [profits, setShares] = useRecoilState(profitSharesState(accountId))

  const addProfit = (deposit: IProfitShare) => {
    setShares([...profits, deposit])
  }

  return addProfit
}

export const useDeleteProfit = (accountId: AccountId) => {
  const [shares, setShares] = useRecoilState(profitSharesState(accountId))

  const deleteProfit = async (profitId: string) => {
    const response = await api.delete(`/profit-share/${profitId}`)

    if (response.status === 200) {
      setShares(shares.filter((item) => item.id !== profitId))
    }
  }

  return deleteProfit
}

type AccountProfitShareKey = {
  accountId: AccountId
  profitShareId: string
}

export const accountProfitSharesIdsState = selectorFamily<string[], AccountId>({
  key: 'accountProfitSharesIdsState',
  get:
    (accountId: AccountId) =>
    ({ get }) => {
      const profitShares = get(profitSharesState(accountId))

      return profitShares.filter((item) => item.account.id === accountId).map((item) => item.id)
    },
})

export const accountProfitShareState = selectorFamily<
  IProfitShare | undefined,
  AccountProfitShareKey
>({
  key: 'accountProfitShareState',
  get:
    ({ accountId, profitShareId }) =>
    ({ get }) => {
      const shares = get(profitSharesState(accountId))
      return shares.find((item) => item.id === profitShareId)
    },
})

export const accountTotalProfitsState = selectorFamily({
  key: 'accountTotalProfitsState',
  get:
    (accountId: AccountId) =>
    ({ get }) => {
      const profitShares = get(accountProfitSharesIdsState(accountId))
      const profitShareValues = profitShares.map((profitShareId) =>
        get(accountProfitShareState({ accountId, profitShareId })),
      )

      const totalProfits = profitShareValues.reduce((acc, curr) => {
        if (curr) {
          return acc + parseFloat(String(curr.amount))
        }
        return acc
      }, 0)
      return totalProfits
    },
})